import { useContext, useEffect } from 'react';
import { path, nth, prepend } from 'ramda';
import { internalErrorBusinessComp } from '../../../../../Components/InternalError/InternalError';
import { valjTyp } from '../../../../../Utils/ObjektVyUtils';
import NodeQuery from '../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import {
  IObjektVy,
  IBusinessCollectionProps,
} from '../../../../../Types/ObjektVy';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import { findCollectionStaticSafe } from '../../../../../Model/avenyStaticModelUtils';
import { MdModalRoute } from '../../../../../Components/MasterDetail/Components/MdModal/MdModalRoute';
import {
  IMasterCompProps,
  IDetailCompProps,
} from '../../../../../Types/MasterDetailTypes';
import { createPortal } from 'react-dom';
import BusinessDataObjectDetailHeader from './BusinessDataObjectDetailHeader/BusinessDataObjectDetailHeader';
import { StandardVy } from './StandardVy';
import EDIconButton from '../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import { BreadcrumbsContext } from '../RutinBreadcrumbs/BreadcrumbsContext';
import {
  NewId,
  selectedId,
} from '../../../../../Components/MasterDetail/MasterDetailUtils';

const id = (data: any | null, associationName: string): string | null =>
  path(['_Associations', associationName, 'id'], data) || null;

/**
Skapar en vy för ett associerat objekt.
*/
export const associeradObjektVy = (
  associationName: string,
  /** ange en vy för det associerade objektet om man inte vill använda standardvyn */
  vy?: IObjektVy,
  detaljVy?: IObjektVy,
  fullPage?: boolean,
  vyName?: string,
  objectTypename?: string
): IObjektVy => ({
  type: 'ObjektVy',
  id: `${vyName ?? associationName}`,
  info: { komponentMedRubrik: true },
  def: (businessModel: IClientBusinessModel) => {
    const assoc = businessModel.Associations[associationName];
    if (assoc) {
      const displaylabel = assoc.DisplayLabel;
      const assocBmod: IClientBusinessModel = findCollectionStaticSafe(
        assoc.AssociatedCollectionName,
        'felaktig association'
      );

      const VyComp = (vy || valjTyp('standard')).def(assocBmod);
      const dataDependencies = (VyComp.dataDependencies ?? []).map(
        dep => `${associationName}.${dep}`
      );
      return {
        caption: displaylabel,
        dataDependencies,
        comp: (props: IBusinessCollectionProps) => {
          const parents = prepend(
            { id: props.id, associationName },
            props.config?.parents ?? []
          );
          return (
            <NodeQuery
              collectionName={businessModel.CollectionName}
              id={selectedId(props.id) === 'ny' ? null : selectedId(props.id)}
              fieldDefs={[`${associationName}.id`]}
            >
              {(loading, data) => {
                const associatedId = loading
                  ? null
                  : id(data, associationName) ?? NewId;

                const objectId =
                  objectTypename != null && data?.__typename === objectTypename
                    ? props.id
                    : associatedId;

                return detaljVy ? (
                  <MdModalRoute
                    detailRouteName={associationName}
                    master={{
                      caption: businessModel.RootType.SBLabel,
                      comp: (masterprops: IMasterCompProps) => {
                        const {
                          headerReferences,
                          currentNavLevel,
                          topNavLevel,
                          backButtonReferences,
                          registerActiveNavLevel,
                          unregisterActiveNavLevel,
                          // eslint-disable-next-line react-hooks/rules-of-hooks
                        } = useContext(BreadcrumbsContext);
                        const headerRef =
                          masterprops.selectedDetail &&
                          currentNavLevel !== undefined
                            ? headerReferences[currentNavLevel]
                            : undefined;
                        const renderHeader = headerRef != null;
                        const backRef =
                          masterprops.selectedDetail &&
                          currentNavLevel !== undefined &&
                          currentNavLevel === topNavLevel
                            ? nth(currentNavLevel, backButtonReferences) || null
                            : null;
                        const unselectDetail = () =>
                          masterprops.selectDetail(null);
                        const selectCurrentDetail = () =>
                          masterprops.selectDetail(masterprops.selectedDetail);

                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                          if (renderHeader && currentNavLevel) {
                            registerActiveNavLevel(currentNavLevel);
                            return () =>
                              unregisterActiveNavLevel(currentNavLevel);
                          }
                          return undefined;
                        }, [
                          renderHeader,
                          currentNavLevel,
                          registerActiveNavLevel,
                          unregisterActiveNavLevel,
                        ]);
                        return (
                          <>
                            <div
                              style={{
                                display: 'flex',
                              }}
                            >
                              <EDTypography
                                variant="overline"
                                color="textSecondary"
                                bold
                              >
                                {displaylabel}
                              </EDTypography>
                              {associatedId !== null && (
                                <EDIconButton
                                  tooltip="Mer information"
                                  icon="info"
                                  style={{ margin: '0 0 0 4px', padding: 0 }}
                                  onClick={() =>
                                    masterprops.selectDetail(associatedId)
                                  }
                                />
                              )}
                            </div>
                            <VyComp.comp
                              id={id(data, associationName)}
                              config={{ parents }}
                            />
                            <BusinessDataObjectDetailHeader
                              collectionName={assoc.AssociatedCollectionName}
                              id={associatedId}
                              headerRef={headerRef}
                              onClick={
                                currentNavLevel !== topNavLevel
                                  ? selectCurrentDetail
                                  : undefined
                              }
                            />
                            {backRef &&
                              createPortal(
                                <EDIconButton
                                  icon="arrow_back"
                                  color="inherit"
                                  tooltip={`Tillbaka till ${businessModel.RootType.SBLabel}`}
                                  padding="dense"
                                  marginTop={0.5}
                                  marginBottom={0.5}
                                  onClick={unselectDetail}
                                />,
                                backRef
                              )}
                          </>
                        );
                      },
                    }}
                    detail={{
                      caption: assoc.DisplayLabel,
                      comp: (_detailprops: IDetailCompProps) => (
                        <StandardVy bmod={assocBmod} id={associatedId} />
                      ),
                    }}
                    collection={assocBmod}
                  />
                ) : (
                  <VyComp.comp
                    id={objectId}
                    config={{ fullPage: true, parents }}
                    isNew={objectId === 'ny'}
                  />
                );
              }}
            </NodeQuery>
          );
        },
      };
    }
    return {
      caption: `${associationName} - fel`,
      id: 'felvy',
      comp: internalErrorBusinessComp(
        'e4f81b51-3486-41f9-9a83-c03362589135',
        `${associationName} saknas`
      ),
    };
  },
});

/** @component */
export default associeradObjektVy;
